/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { LoggerService } from '../../../../shared/services/logger.service';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { SelectUploader } from './state/upload.actions';
import { FileUploadDialogData, StoredFile, UploaderType } from './state/upload.model';
import { UploaderState } from './state/upload.state';
import { from, Observable } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { StorageService } from '../../../../shared/services/storage.service';
import { DataFormService } from '../../../services/data-form.service';
import { DeviceService } from '../../../../shared/services/device.service';
import { DataFormContentComponent } from '../../../dataForm/data-form-content.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FileUploadComponent } from '../../../../../core/ui/file-upload/file-upload.component';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'dash-gis-upload-dialog',
    templateUrl: './upload-dialog.component.html',
    styleUrls: ['./upload-dialog.component.scss'],
    imports: [
        CdkScrollable,
        MatDialogContent,
        FileUploadComponent,
        LayoutDirective,
        LayoutAlignDirective,
        MatIconButton,
        MatIcon,
        DataFormContentComponent,
        MatDialogActions,
        MatButton,
    ],
})
export class UploadDialogComponent implements OnInit {
    uploader$: Observable<UploaderType> = inject(Store).select(UploaderState.uploader);

    displayName = 'Upload Files';
    formValid = false;
    filesToUpload: File[] = [];
    uploading = false;

    @ViewChild(DataFormContentComponent) dataFormContent: DataFormContentComponent;

    public constructor(
        private logger: LoggerService,
        private dialogRef: MatDialogRef<UploadDialogComponent>,
        private dataFormService: DataFormService,
        private deviceService: DeviceService,
        private storageService: StorageService,
        private store: Store,
        @Inject(MAT_DIALOG_DATA) public data: FileUploadDialogData
    ) {}

    ngOnInit(): void {
        // check once for camera existing
        // this.deviceService.hasVideoInput$.subscribe(hasVideo => this.hasVideoInput$ = hasVideo);
    }

    useCameraUpload() {
        this.store.dispatch(new SelectUploader({ uploader: UploaderType.CAMERA }));
    }

    useFileUpload() {
        this.store.dispatch(new SelectUploader({ uploader: UploaderType.FILE }));
    }

    setDisplayName(name: string) {
        this.displayName = name;
    }

    isFormValid(valid: boolean) {
        this.formValid = valid;
    }

    shouldCloseDialog(success: boolean) {
        this.dialogRef.close(success);
    }

    cancel() {
        this.dialogRef.close(false);
    }

    /**
     * Add new elements to list of files that will be uploaded, excluding duplicates identified
     * by name and size
     * @param files
     */
    public setFileToUpload(files: File[]) {
        files.forEach((newFile) => {
            if (
                -1 ===
                this.filesToUpload.findIndex(
                    (currentFile) => currentFile.name === newFile.name && currentFile.size === newFile.size
                )
            ) {
                this.filesToUpload.push(newFile);
            }
        });
        this.dataFormContent.dataForm.updateValueAndValidity();
    }

    public doUpload() {
        this.uploading = true;
        const files = this.filesToUpload;
        from(files)
            .pipe(
                concatMap((f) => this.storageService.storeFile(f)),
                map((savedFile) => {
                    if (Array.isArray(savedFile)) {
                        savedFile.forEach((file) => {
                            this.populateDataForm(file);
                        });
                    } else {
                        this.populateDataForm(savedFile);
                    }
                })
            )
            .subscribe({
                next: null,
                error: null,
                complete: () => this.dialogRef.close(true),
            });
    }

    removeFile(index: number) {
        this.filesToUpload.splice(index, 1);
    }

    private populateDataForm(savedFile: StoredFile) {
        const form = this.dataFormContent.dataForm;
        form.controls['hash'].markAsTouched();
        form.controls['hash'].markAsDirty();
        form.controls['hash'].setValue(savedFile.key);
        form.controls['filename'].markAsTouched();
        form.controls['filename'].markAsDirty();
        form.controls['filename'].setValue(savedFile.originalName);
        form.controls['filetype'].markAsTouched();
        form.controls['filetype'].markAsDirty();
        form.controls['filetype'].setValue(savedFile.mimeType);
        form.updateValueAndValidity();

        this.dataFormContent.saveData();
    }

    get canUseCamera() {
        return this.deviceService.canCaptureUserMedia;
    }

    get isReadyToUpload() {
        return this.formValid && this.filesToUpload.length > 0 && !this.uploading;
    }

    get acceptMimeTypes(): string[] {
        return ['image/.+', 'application/pdf'];
    }

    get acceptFileNames(): string[] {
        return [];
    }
}
